import { useOutboxStore } from '@/features/offline/model/outboxStore'
import { STORAGE_REENCRYPT_V1 } from '@/shared/config/featureFlags'
import { clearUserDataStores, purgeLocalAiVectorStoreOnBoot } from '@/shared/lib/clearUserData'

export const STORAGE_REENCRYPT_SESSION_USER_KEY = 'synap-active-storage-user'
export const STORAGE_REENCRYPT_GENERATION_KEY = 'synap-storage-reencrypt-generation'
export const STORAGE_REENCRYPT_GENERATION = 1

/** Evita login preso em "Preparando sessão local..." se IDB/purge travar. */
export const STORAGE_REENCRYPT_TIMEOUT_MS = 8000

const generationKeyForUser = (userId: string) => `${STORAGE_REENCRYPT_GENERATION_KEY}:${userId}`

const raceWithTimeout = async <T>(promise: Promise<T>, ms: number): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    return await Promise.race([
      promise,
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error(`storage reencrypt timeout ${ms}ms`)), ms)
      }),
    ])
  } finally {
    if (timer !== undefined) clearTimeout(timer)
  }
}

/**
 * RW-S01 — hook mínimo pós-login: aplica bump de geração de criptografia (uma vez por userId),
 * marca sessão ativa e reforça TTL de vetores locais.
 * Futuro: pass Web Crypto keyed por userId+session sobre STORAGE_KEYS.
 */
export const maybeReencryptLocalStorageOnLogin = async (userId: string): Promise<void> => {
  if (!STORAGE_REENCRYPT_V1 || !userId) return
  if (typeof window === 'undefined') return

  try {
    await raceWithTimeout(
      (async () => {
        const generationKey = generationKeyForUser(userId)
        const storedGeneration = Number(localStorage.getItem(generationKey) ?? '0')

        if (storedGeneration < STORAGE_REENCRYPT_GENERATION) {
          const pendingOutboxCount = useOutboxStore.getState().items.length
          await clearUserDataStores({ preserveOutbox: pendingOutboxCount > 0 })
          try {
            localStorage.setItem(generationKey, String(STORAGE_REENCRYPT_GENERATION))
          } catch {
            /* quota — não bloquear login */
          }
        }

        sessionStorage.setItem(STORAGE_REENCRYPT_SESSION_USER_KEY, userId)
        await purgeLocalAiVectorStoreOnBoot()
      })(),
      STORAGE_REENCRYPT_TIMEOUT_MS,
    )
  } catch {
    try {
      sessionStorage.setItem(STORAGE_REENCRYPT_SESSION_USER_KEY, userId)
    } catch {
      /* ignore */
    }
  }
}

/** Limpa marcadores de sessão de re-criptografia (logout). */
export const clearStorageReencryptSessionMarkers = (): void => {
  if (typeof window === 'undefined') return
  sessionStorage.removeItem(STORAGE_REENCRYPT_SESSION_USER_KEY)
}

export const clearStorageReencryptUserGeneration = (userId: string): void => {
  if (typeof window === 'undefined' || !userId) return
  localStorage.removeItem(generationKeyForUser(userId))
}
